/** Session keys + handoff for buyer landing → conversation page.
 * Buyer and seller paths keep separate conversation pointers so a seller CTA
 * cannot steal card-tap / browse traffic (and vice versa).
 */

export function normalizeConvPathMode(pathMode) {
  return pathMode === 'seller' ? 'seller' : 'buyer';
}

export function legacyConvStorageKey(agentSlug) {
  return `a4a_conv_${agentSlug}`;
}

export function convStorageKey(agentSlug, pathMode = 'buyer') {
  const mode = normalizeConvPathMode(pathMode);
  return mode === 'seller'
    ? `a4a_conv_seller_${agentSlug}`
    : `a4a_conv_buyer_${agentSlug}`;
}

/** Drop the pre-dual-key pointer so a seller id cannot be resumed as buyer. */
export function scrubLegacyConversationId(agentSlug) {
  if (!agentSlug) return;
  try {
    localStorage.removeItem(legacyConvStorageKey(agentSlug));
  } catch { /* ignore */ }
}

/** @deprecated Agent-scoped key — leaked hands-free across new conversations. */
export function legacyHandsFreeStorageKey(agentSlug) {
  return `a4a_handsfree_${agentSlug}`;
}

/** Hands-free is per conversation so "New conversation" never inherits mic/TTS. */
export function handsFreeStorageKey(agentSlug, conversationId) {
  if (!agentSlug || !conversationId) return null;
  return `a4a_handsfree_${agentSlug}_${conversationId}`;
}

export function handoffStorageKey(agentSlug) {
  return `a4a_chat_handoff_${agentSlug}`;
}

export function getStoredConversationId(agentSlug, pathMode = 'buyer') {
  if (!agentSlug) return null;
  const mode = normalizeConvPathMode(pathMode);
  try {
    scrubLegacyConversationId(agentSlug);
    return localStorage.getItem(convStorageKey(agentSlug, mode)) || null;
  } catch {
    return null;
  }
}

export function setStoredConversationId(agentSlug, conversationId, pathMode = 'buyer') {
  if (!agentSlug || !conversationId) return;
  const mode = normalizeConvPathMode(pathMode);
  try {
    scrubLegacyConversationId(agentSlug);
    localStorage.setItem(convStorageKey(agentSlug, mode), conversationId);
  } catch { /* ignore */ }
}

export function clearStoredConversationId(agentSlug, pathMode = null) {
  if (!agentSlug) return;
  try {
    scrubLegacyConversationId(agentSlug);
    if (pathMode === 'buyer' || pathMode === 'seller') {
      localStorage.removeItem(convStorageKey(agentSlug, pathMode));
      return;
    }
    // No mode → clear both path pointers (e.g. hard reset).
    localStorage.removeItem(convStorageKey(agentSlug, 'buyer'));
    localStorage.removeItem(convStorageKey(agentSlug, 'seller'));
  } catch { /* ignore */ }
}

export function clearLegacyHandsFreePreference(agentSlug) {
  if (!agentSlug) return;
  try {
    sessionStorage.removeItem(legacyHandsFreeStorageKey(agentSlug));
  } catch { /* ignore */ }
}

export function setHandsFreePreference(agentSlug, on, conversationId = null) {
  if (!agentSlug) return;
  try {
    // Never write the old agent-scoped key — it caused cross-conversation leaks.
    clearLegacyHandsFreePreference(agentSlug);
    const key = handsFreeStorageKey(agentSlug, conversationId);
    if (!key) return;
    if (on) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  } catch { /* ignore */ }
}

export function getHandsFreePreference(agentSlug, conversationId = null) {
  if (!agentSlug || !conversationId) return false;
  try {
    clearLegacyHandsFreePreference(agentSlug);
    return sessionStorage.getItem(handsFreeStorageKey(agentSlug, conversationId)) === '1';
  } catch {
    return false;
  }
}

export function clearHandsFreePreference(agentSlug, conversationId = null) {
  if (!agentSlug) return;
  try {
    clearLegacyHandsFreePreference(agentSlug);
    const key = handsFreeStorageKey(agentSlug, conversationId);
    if (key) sessionStorage.removeItem(key);
  } catch { /* ignore */ }
}

export function stashHandoff(agentSlug, payload) {
  if (!agentSlug || !payload) return;
  try {
    sessionStorage.setItem(handoffStorageKey(agentSlug), JSON.stringify(payload));
  } catch { /* ignore */ }
}

/** Read and clear one-shot handoff (navigation state may already have it). */
export function takeHandoff(agentSlug) {
  if (!agentSlug) return null;
  try {
    const raw = sessionStorage.getItem(handoffStorageKey(agentSlug));
    sessionStorage.removeItem(handoffStorageKey(agentSlug));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
